// Onboarding actions. Components call these instead of touching the store: each
// one calls the traceira-backend (real, read-only DNS) and maps its answer into
// onboarding state.

import { announce } from '../../state/announcer.ts';
import { useSession, type WorkspaceSession } from '../../state/session.ts';
import {
  api,
  ApiError,
  type ContainerView,
  type ServerCheck,
  type ServerLine,
  type ServerSession,
} from './api.ts';
import * as model from './model.ts';
import { createPacer, type Pacer } from './pacing.ts';
import {
  CHECK_INTERVAL_MS,
  RECORD_NAME,
  dashboardUrlFor,
  genericFields,
  route53Fields,
  type Provider,
} from './provider.ts';
import { act, useOnboarding } from './store.ts';

const get = () => useOnboarding.getState();

let lineSeq = 0;
const nextId = () => `l${(lineSeq += 1)}`;
const toLine = (l: ServerLine): model.LogLine => ({
  id: nextId(),
  time: l.time,
  glyph: l.glyph as model.Glyph,
  text: l.text,
  ...(l.duration ? { duration: l.duration } : {}),
  ...(l.group === undefined ? {} : { group: l.group }),
});
const localLine = (glyph: model.Glyph, text: string, group?: number): model.LogLine => ({
  id: nextId(),
  time: new Date().toISOString().slice(11, 23),
  glyph,
  text,
  ...(group === undefined ? {} : { group }),
});

const hhmmss = (iso: string | null) => (iso ? `${iso.slice(11, 19)} UTC` : null);

function toProvider(check: ServerCheck, domain: string): Provider {
  const p = check.provider;
  const name = p?.name ?? '';
  return {
    kind: p?.kind ?? 'unknown',
    name,
    shortName: p?.shortName ?? '',
    nameservers: check.authoritative
      ? [
          check.authoritative.host,
          ...check.nameservers.filter((n) => n !== check.authoritative?.host),
        ]
      : check.nameservers,
    path: p?.path?.map((part) => part.replace('{domain}', domain)) ?? null,
    fields: name.startsWith('Route 53') ? route53Fields : genericFields,
    hasProxy: p?.hasProxy ?? false,
    dashboardUrl: dashboardUrlFor(name, check.zone ?? domain),
  };
}

const CHECK_STATUS: Record<ServerCheck['outcome'], model.CheckStatus> = {
  complete: 'complete',
  missing: 'missing',
  timeout: 'timeout',
  unreachable: 'timeout',
  unsafe: 'timeout',
  stopped: 'stopped',
};

const RECORD_STATUS: Record<ServerSession['recordState']['status'], model.RecordStatus> = {
  'not-checked': 'not-checked',
  waiting: 'waiting',
  verified: 'verified',
  proxy: 'proxy',
  wrong: 'wrong',
  conflict: 'conflict',
  error: 'waiting',
};

/** Applies a backend session to local state (never the typed Step 1 input). */
function applySession(session: ServerSession, extra: Partial<model.OnboardingState> = {}) {
  act((s) => {
    const server: model.ServerData = {
      sessionId: session.id,
      workspaceId: session.workspaceId,
      cname: session.record.type === 'CNAME' ? session.record.value : s.server.cname,
      txt: session.record.type === 'TXT' ? session.record.value : s.server.txt,
      methods: session.methods,
      found: session.recordState.found,
      resolvers: session.recordState.resolvers,
      nameInUse: session.check?.nameConflict != null,
      error: null,
      busy: false,
      domainConnect: s.server.domainConnect,
    };
    const attempts = session.recordState.attempts;
    const timedOut =
      session.recordState.status !== 'verified' && attempts >= model.TEN_MINUTES_ATTEMPT;
    return {
      ...s,
      domain: session.domain,
      ...(session.authorizer
        ? { authorizerName: session.authorizer.name, authorizerEmail: session.authorizer.email }
        : {}),
      ...(session.check ? { provider: toProvider(session.check, session.domain) } : {}),
      recordType: session.record.type,
      recordStatus: RECORD_STATUS[session.recordState.status],
      attempts,
      timedOut,
      paused: timedOut ? true : s.paused,
      verifiedAt: hhmmss(session.verifiedAt),
      consent: session.consent
        ? {
            user: session.consent.actor,
            domain: session.domain,
            recordName: RECORD_NAME,
            time: hhmmss(session.consent.acceptedAt) ?? '',
            statementVersion: session.consent.statementVersion,
          }
        : null,
      domainChanged: session.domainChanged,
      server,
      ...extra,
    };
  });
}

function requestFailed(error: unknown) {
  const message =
    error instanceof ApiError ? error.message : 'Something went wrong. Your input is saved.';
  act((s) => ({ ...s, server: { ...s.server, error: message, busy: false } }));
  announce(message);
  return message;
}

/** Backend spec §5.5 item 3: on 429 wait Retry-After seconds, otherwise `fallbackMs`. */
function retryDelayMs(error: unknown, fallbackMs: number): number {
  if (error instanceof ApiError && error.retryAfterSeconds !== null) {
    return Math.max(1, error.retryAfterSeconds) * 1000;
  }
  return fallbackMs;
}

let checkAbort: AbortController | null = null;
let checkPacer: Pacer | null = null;
let containerPollTimer: ReturnType<typeof setTimeout> | null = null;
let containerPollingSessionId: string | null = null;
/** Last container notice announced, keyed by session and code@at (§10.14: step-level only). */
let lastAnnouncedNotice: string | null = null;

function clearContainerPolling() {
  if (containerPollTimer) {
    clearTimeout(containerPollTimer);
    containerPollTimer = null;
  }
  containerPollingSessionId = null;
}

/** Announces a terminal container outcome once per distinct notice (never per poll). */
function announceContainerOutcome(sessionId: string, view: ContainerView) {
  if (view.phase === 'running') {
    announce('Workspace container running. Ready for workspace.');
    return;
  }
  if (!view.notice) return;
  const key = `${sessionId}:${view.notice.code}@${view.notice.at}`;
  if (key === lastAnnouncedNotice) return;
  lastAnnouncedNotice = key;
  announce(`${view.notice.title}: ${view.notice.message}`);
}

function pollContainer(sessionId: string, delayMs: number) {
  clearContainerPolling();
  containerPollingSessionId = sessionId;
  containerPollTimer = setTimeout(async () => {
    if (containerPollingSessionId !== sessionId) return;
    try {
      const view = await api.getContainer(sessionId);
      if (containerPollingSessionId !== sessionId) return;
      act((s) => ({ ...s, container: view }));
      announceContainerOutcome(sessionId, view);
      if (view.pollAfterMs !== null) {
        pollContainer(sessionId, view.pollAfterMs);
      }
    } catch (error) {
      if (containerPollingSessionId !== sessionId) return;
      if (error instanceof ApiError && error.status === 429) {
        pollContainer(sessionId, retryDelayMs(error, 10000));
      } else {
        // Backend spec §5.5 item 4: Network error or 5xx on a GET. Keep last view and retry after 10 000 ms.
        pollContainer(sessionId, 10000);
      }
    }
  }, delayMs);
}

async function startContainer(sessionId: string) {
  clearContainerPolling();
  // A start or Retry is a fresh interaction: its outcome may be announced again.
  lastAnnouncedNotice = null;
  // Mark in-flight creating state per backend spec §5.4 if not already terminal or has stages
  act((s) => ({
    ...s,
    container:
      s.container && s.container.stages.length > 0
        ? { ...s.container, phase: 'creating', canStart: false, pollAfterMs: 3000 }
        : model.inFlightContainerView(),
  }));

  try {
    const view = await api.createContainer(sessionId);
    // Start over may have replaced the session while the POST was in flight.
    if (get().server.sessionId !== sessionId) return;
    act((s) => ({ ...s, container: view }));
    announceContainerOutcome(sessionId, view);
    if (view.pollAfterMs !== null) {
      pollContainer(sessionId, view.pollAfterMs);
    }
  } catch (error) {
    if (get().server.sessionId !== sessionId) return;
    const msg = error instanceof ApiError ? error.message : "We can't reach Traceira right now.";
    act((s) => ({
      ...s,
      container: {
        phase: 'unavailable',
        stages: s.container?.stages ?? [],
        attempt: s.container?.attempt ?? null,
        createdAt: s.container?.createdAt ?? null,
        readyDeadline: s.container?.readyDeadline ?? null,
        expiresAt: s.container?.expiresAt ?? null,
        observedAt: new Date().toISOString(),
        stale: false,
        canStart: true,
        pollAfterMs: null,
        notice: {
          code: 'unreachable',
          title: 'Workspace service unreachable',
          message: msg,
          at: new Date().toISOString(),
        },
      },
    }));
  }
}

/** Reveals one domain-check line and announces each sub-step as it finishes. */
function revealCheckLine(line: model.LogLine) {
  const before = model.checkSubSteps(get()).filter((x) => x.status === 'done').length;
  act((s) => ({ ...s, checkLines: [...s.checkLines, line] }));
  const steps = model.checkSubSteps(get());
  const after = steps.filter((x) => x.status === 'done').length;
  const done = after > before ? steps[after - 1] : undefined;
  if (done) announce(`${done.label}: ${done.detail}`, { channel: 'check', throttleMs: 1000 });
}

async function runDomainCheck() {
  const state = get();
  const { valid, domain } = model.validateStep1(state);
  if (!valid || !domain) return;
  const input = {
    organization: state.organization.trim(),
    authorizer: {
      name: state.authorizerName.trim(),
      email: state.authorizerEmail.trim().toLowerCase(),
    },
    domain: state.typedDomain,
    useParentDomain: state.subdomainChoice === 'parent',
    ...(state.organizationId ? { organizationId: state.organizationId } : {}),
  };
  checkAbort?.abort();
  checkPacer?.cancel();
  const controller = new AbortController();
  const pacer = createPacer(revealCheckLine);
  checkAbort = controller;
  checkPacer = pacer;
  try {
    const sessionId = get().server.sessionId;
    const session = sessionId
      ? await api.updateSession(sessionId, input)
      : await api.createSession(input);
    if (controller.signal.aborted) return;
    applySession(session, { check: 'running', checkLines: [], domain: session.domain });
    // Concurrently start container creation per spec §4.2 & acceptance criteria 2. An anonymous
    // visitor gets one only after proving domain control (recordCheck starts it then).
    if (useSession.getState().actor) void startContainer(session.id);
    const result = await api.domainCheck(session.id, controller.signal, (l) =>
      pacer.push(toLine(l)),
    );
    if (!result) return;
    // The outcome shows only after the reader has seen every call.
    await pacer.drained();
    if (controller.signal.aborted) return;
    act((s) => ({
      ...s,
      check: CHECK_STATUS[result.outcome],
      provider: toProvider(result, session.domain),
      server: { ...s.server, nameInUse: result.nameConflict !== null },
    }));
    const s = get();
    const provider =
      s.provider?.kind === 'unknown' ? 'Provider not recognized' : `${s.provider?.name} detected`;
    announce(
      s.check === 'complete'
        ? `Domain check complete. ${provider}.`
        : s.check === 'missing'
          ? `No DNS servers found for ${s.domain}. Edit the domain.`
          : 'The DNS lookup did not finish. Run the check again.',
    );
  } catch (error) {
    if (controller.signal.aborted) return;
    pacer.flush();
    const message = requestFailed(error);
    act((s) => ({
      ...s,
      check: 'timeout',
      checkLines: [...s.checkLines, localLine('✕', message)],
    }));
    if (error instanceof ApiError && error.status === 422) {
      const errors = (error.body.errors ?? {}) as model.OnboardingState['errors'];
      act((s) => ({ ...s, errors, check: 'idle' }));
    }
  } finally {
    if (checkAbort === controller) checkAbort = null;
    if (checkPacer === pacer) checkPacer = null;
  }
}

const PROBLEM: Record<string, string> = {
  proxy: 'Problem: the proxy is on for this record.',
  wrong: 'Problem: the record points somewhere else.',
  conflict: 'Problem: another record already uses this name.',
};

async function recordCheck(manual: boolean) {
  const id = get().server.sessionId;
  if (!id || get().server.busy) return;
  act((s) => ({ ...s, server: { ...s.server, busy: true } }));
  const before = get().recordStatus;
  const channel = manual ? {} : { channel: 'record', throttleMs: CHECK_INTERVAL_MS };
  try {
    const { session, lines } = await api.checkRecord(id);
    applySession(session, { recordLog: [...get().recordLog, ...lines.map(toLine)] });
    const s = get();
    if (s.recordStatus === 'verified' && before !== 'verified') {
      announce(`Record found. ${s.domain} is verified.`, channel);
      if (!useSession.getState().actor && !s.container) void startContainer(id);
    } else if (s.timedOut) {
      announce('Still not found after 10 minutes. Checking is paused.', channel);
    } else if (manual || s.recordStatus !== before) {
      announce(PROBLEM[s.recordStatus] ?? 'Waiting for your record.', channel);
    }
  } catch (error) {
    // Too many checks: the next automatic check will try again.
    if (error instanceof ApiError && error.status === 429) {
      act((s) => ({ ...s, server: { ...s.server, busy: false } }));
      return;
    }
    requestFailed(error);
  }
}

/** Asks whether the DNS provider can add the record for the person (Domain Connect). */
async function loadDomainConnect() {
  const id = get().server.sessionId;
  if (!id) return;
  try {
    const dc = await api.domainConnect(id);
    act((s) => ({
      ...s,
      server: {
        ...s.server,
        domainConnect: {
          status: dc.status,
          providerName: dc.provider?.name ?? '',
          controlPanelUrl: dc.provider?.controlPanelUrl ?? null,
          method: dc.method,
          applyUrl: dc.applyUrl,
          templateApproved: dc.templateApproved,
          opened: false,
        },
      },
    }));
  } catch {
    // Optional: without it, the manual record card is the way.
    act((s) => ({ ...s, server: { ...s.server, domainConnect: null } }));
  }
}

async function create() {
  const id = get().server.sessionId;
  if (!id) return;
  try {
    const workspace = await api.createWorkspace(id);
    act((s) => ({
      ...s,
      workspace: 'ready',
      server: { ...s.server, workspaceId: workspace.id },
      container: workspace.container ?? s.container,
    }));
    announce('Domain setup saved. Prepare authorization before entering your workspace.');
  } catch (error) {
    requestFailed(error);
    act((s) => ({ ...s, workspace: 'failed' }));
    announce(
      `We couldn't set up ${get().organization} just now. Your domain verification is saved.`,
    );
  }
}

export const onboarding = {
  /** Step 1 → domain check. Returns false when input is invalid. */
  startCheck(): boolean {
    const { state: validated, valid, domain } = model.validateStep1(get());
    if (!valid || !domain) {
      act(() => validated);
      return false;
    }
    // Show the check immediately; the backend answers into it as lines arrive.
    act(() => ({ ...validated, domain, check: 'running', checkLines: [], provider: null }));
    void runDomainCheck();
    return true;
  },

  retryCheck() {
    onboarding.startCheck();
  },

  stopCheck() {
    checkAbort?.abort();
    checkAbort = null;
    // Calls that already ran stay in the log; only the waiting is skipped.
    checkPacer?.flush();
    checkPacer = null;
    act((s) => ({
      ...s,
      check: 'stopped',
      checkLines: [
        ...s.checkLines,
        localLine('■', 'Stopped by you', s.checkLines.at(-1)?.group ?? 0),
      ],
    }));
    announce('Domain check stopped. Your input is saved.');
  },

  /** Domain check → Step 2: runs the first record check before navigating. */
  async connect(): Promise<boolean> {
    // The check step is done: stop polling the container (the gate already saw
    // a terminal phase; a stale-unavailable loop must not follow the person).
    clearContainerPolling();
    act((s) => ({ ...s, recordLog: [], paused: false, timedOut: false }));
    await recordCheck(false);
    void loadDomainConnect();
    return get().attempts > 0;
  },

  async checkNow() {
    act((s) => ({ ...s, paused: false }));
    await recordCheck(true);
  },

  /** The person opened the provider (approval or DNS page); we check again when they return. */
  domainConnectOpened() {
    const dc = get().server.domainConnect;
    act((s) => ({
      ...s,
      paused: false,
      server: {
        ...s.server,
        domainConnect: s.server.domainConnect && { ...s.server.domainConnect, opened: true },
      },
    }));
    const provider = dc?.providerName || 'your DNS provider';
    announce(
      dc?.templateApproved
        ? `Approve the record in ${provider}. We'll keep checking here.`
        : `Add the record in ${provider}. We'll keep checking here.`,
    );
  },

  /** Back on the setup tab: check at once instead of waiting for the next 8 s tick. */
  returnedToTab() {
    const s = get();
    if (s.recordStatus === 'verified' || !s.server.sessionId) return;
    void recordCheck(true);
  },

  /** The timer hook calls this every 8 s on Step 2. */
  autoCheck() {
    const s = get();
    if (s.paused || s.timedOut || s.recordStatus === 'verified') return;
    void recordCheck(false);
  },

  resumeAfterTimeout() {
    act((s) => ({ ...s, paused: false, timedOut: false }));
    void recordCheck(true);
  },

  togglePause() {
    const paused = get().paused;
    act((s) => ({
      ...s,
      paused: !paused,
      recordLog: [
        ...s.recordLog,
        paused ? localLine('·', 'Automatic checks resumed') : localLine('■', 'Paused by you'),
      ],
    }));
    announce(paused ? 'Automatic checks resumed.' : 'Automatic checks paused.');
  },

  async setRecordType(type: model.RecordType) {
    const id = get().server.sessionId;
    if (!id) return;
    try {
      applySession(await api.setMethod(id, type), { recordLog: [] });
      await recordCheck(false);
      void loadDomainConnect();
    } catch (error) {
      requestFailed(error);
      return;
    }
    announce(`Switched to a ${type} record. The check now looks for it.`);
  },

  async setConsent(checked: boolean) {
    const id = get().server.sessionId;
    if (!id || get().consentSaving) return;
    const previous = get().consent;
    // Show the selected state immediately, but don't allow workspace creation
    // until the backend confirms the versioned consent record.
    act((s) => ({
      ...s,
      consentSaving: true,
      consent: checked
        ? {
            user: 'You',
            domain: s.domain,
            recordName: RECORD_NAME,
            time: '',
            statementVersion: '',
          }
        : null,
    }));
    try {
      const session = await api.setConsent(id, checked);
      if (get().server.sessionId !== id) return;
      applySession(session, { consentSaving: false });
    } catch (error) {
      if (get().server.sessionId !== id) return;
      act((s) => ({ ...s, consent: previous, consentSaving: false }));
      requestFailed(error);
    }
  },

  /** Step 2 → Step 3. */
  async createWorkspace() {
    if (!model.canContinue(get())) return;
    act((s) => ({ ...s, workspace: 'creating', creationAttempts: s.creationAttempts + 1 }));
    await create();
  },

  async retryWorkspace() {
    if (get().workspace !== 'failed') return;
    act((s) => ({ ...s, workspace: 'creating', creationAttempts: s.creationAttempts + 1 }));
    await create();
  },

  retryContainer() {
    const id = get().server.sessionId;
    if (!id) return;
    void startContainer(id);
  },

  /** Marks onboarding complete and returns the session handed to the workspace. */
  pendingWorkspace(s = get()): WorkspaceSession | null {
    if (s.workspace !== 'ready' || !s.consent || !s.verifiedAt || !s.server.workspaceId)
      return null;
    return {
      id: s.server.workspaceId,
      ...(s.server.sessionId ? { setupSessionId: s.server.sessionId } : {}),
      name: s.organization,
      ...(s.authorizerName && s.authorizerEmail
        ? { authorizer: { name: s.authorizerName, email: s.authorizerEmail } }
        : {}),
      domain: s.domain,
      verifiedThrough: `DNS record on ${s.provider?.nameservers[0] ?? ''}`,
      verifiedAt: s.verifiedAt,
      consent: { user: s.consent.user, time: s.consent.time },
      container: s.container ?? undefined,
    };
  },

  /** Clears onboarding and cancels any running check. */
  reset() {
    checkAbort?.abort();
    checkAbort = null;
    checkPacer?.cancel();
    checkPacer = null;
    clearContainerPolling();
    useOnboarding.setState(model.initialState(), true);
  },
};
